import { WIKIMEDIA_FLAG_ASSETS } from '@/lib/wikimedia-flag-assets';

export type StateFlagAssetStatus =
  'verified-official' | 'wikimedia-fallback' | 'official-source-blocked' | 'needs-official-source';

export interface StateFlagAsset {
  code: string;
  status: StateFlagAssetStatus;
  imagePath?: string;
  imageUrl?: string;
  sourcePageUrl?: string;
  sourceLabel: string;
  format?: 'png' | 'jpg' | 'jpeg';
  notes?: string;
}

const STATE_FLAG_ASSETS: Record<string, StateFlagAsset> = {
  AL: {
    code: 'AL',
    status: 'verified-official',
    imagePath: '/flags/official/AL.jpg',
    imageUrl: 'https://digital.archives.alabama.gov/digital/api/singleitem/collection/photo/id/24334/thumbnail',
    sourcePageUrl: 'https://digital.archives.alabama.gov/digital/collection/photo/id/24334/',
    sourceLabel: 'Alabama Department of Archives and History digital archives',
    format: 'jpg',
    notes: 'Official archive thumbnail; source metadata includes the Alabama archives use agreement form.',
  },
  AK: {
    code: 'AK',
    status: 'verified-official',
    imagePath: '/flags/official/AK.jpg',
    imageUrl: 'https://alaska.gov/kids/images/akflag.jpg',
    sourcePageUrl: 'https://alaska.gov/kids/student.htm',
    sourceLabel: 'State of Alaska Kids Corner state symbols page',
    format: 'jpg',
  },
  AR: {
    code: 'AR',
    status: 'verified-official',
    imagePath: '/flags/official/AR.jpg',
    imageUrl: 'https://ee-sos-site.ark.org/uploads/education/AR_Flag.jpg',
    sourcePageUrl: 'https://www.sos.arkansas.gov/education/arkansas-history/history-of-the-flag',
    sourceLabel: 'Arkansas Secretary of State history of the flag page',
    format: 'jpg',
  },
  AZ: {
    code: 'AZ',
    status: 'verified-official',
    imagePath: '/flags/official/AZ.png',
    imageUrl: 'https://apps.azlibrary.gov/files/img/arizona-state-flag.png',
    sourcePageUrl: 'https://azlibrary.gov/dazl/almanac/azflag',
    sourceLabel: 'Arizona State Library state flag page',
    format: 'png',
  },
  CA: {
    code: 'CA',
    status: 'verified-official',
    imagePath: '/flags/official/CA.jpg',
    imageUrl: 'https://www.library.ca.gov/wp-content/uploads/2021/08/flag.jpg',
    sourcePageUrl: 'https://www.library.ca.gov/california-history/state-symbols/',
    sourceLabel: 'California State Library state symbols page',
    format: 'jpg',
  },
  CO: {
    code: 'CO',
    status: 'verified-official',
    imagePath: '/flags/official/CO.png',
    imageUrl: 'https://www.codot.gov/assets/images/global/coloflag.gif/@@download/image/coloflag.gif',
    sourcePageUrl: 'https://www.codot.gov/assets/images/global/coloflag.gif/view',
    sourceLabel: 'Colorado Department of Transportation official flag image',
    format: 'png',
    notes: 'Official Colorado Department of Transportation GIF retained as a local PNG display derivative.',
  },
  CT: {
    code: 'CT',
    status: 'verified-official',
    imagePath: '/flags/official/CT.jpg',
    imageUrl:
      'https://portal.ct.gov/-/media/connecticut-interactive/images/resources/ctflag240.jpg?h=137&hash=19C228858B5EABC80CB91AECCEF7A268&la=en&rev=7cd4d6cd249845c49bc61ff823a8f06a&w=240',
    sourcePageUrl: 'https://portal.ct.gov/About/State-Symbols',
    sourceLabel: 'CT.gov state symbols page',
    format: 'jpg',
  },
  DE: {
    code: 'DE',
    status: 'verified-official',
    imagePath: '/flags/official/DE.jpg',
    imageUrl: 'https://delaware.gov/wp-content/themes/dosgic_GUIDES_theme/img/facts/state_flag.jpg',
    sourcePageUrl: 'https://delaware.gov/guides/facts/',
    sourceLabel: 'Delaware.gov facts and symbols page',
    format: 'jpg',
  },
  FL: {
    code: 'FL',
    status: 'verified-official',
    imagePath: '/flags/official/FL.jpg',
    imageUrl: 'https://dos.fl.gov/media/8081/flag.jpg',
    sourcePageUrl: 'https://dos.fl.gov/florida-facts/florida-state-symbols/state-flag/',
    sourceLabel: 'Florida Department of State state flag page',
    format: 'jpg',
  },
  GA: {
    code: 'GA',
    status: 'official-source-blocked',
    imageUrl: 'https://sos.ga.gov/sites/default/files/2022/02/03/flag.png',
    sourcePageUrl: 'https://sos.ga.gov/page/staff-directory',
    sourceLabel: 'Georgia Secretary of State press materials',
    format: 'png',
    notes: 'Official PNG/JPG links are published, but Cloudflare blocks clean local file download.',
  },
  HI: {
    code: 'HI',
    status: 'verified-official',
    imagePath: '/flags/official/HI.jpg',
    imageUrl: 'https://ltgov.hawaii.gov/wp-content/uploads/2024/01/Hawaiian-flag-200x300.jpg',
    sourcePageUrl: 'https://ltgov.hawaii.gov/virtual-tour-history-of-the-hawai%CA%BBi-flag/',
    sourceLabel: 'Hawaii Lieutenant Governor history of the Hawaii flag page',
    format: 'jpg',
  },
  ID: {
    code: 'ID',
    status: 'verified-official',
    imagePath: '/flags/official/ID.jpg',
    imageUrl: 'https://sos.idaho.gov/wp-content/uploads/2023/10/st_flag.jpg',
    sourcePageUrl: 'https://sos.idaho.gov/state-emblems/',
    sourceLabel: 'Idaho Secretary of State state emblems page',
    format: 'jpg',
  },
  LA: {
    code: 'LA',
    status: 'verified-official',
    imagePath: '/flags/official/LA.jpg',
    imageUrl: 'https://www.sos.la.gov/media/gglhn5ao/louisianastateflagrgblarge.jpg',
    sourcePageUrl: 'https://www.sos.la.gov/archives/state-flag-and-seal',
    sourceLabel: 'Louisiana Secretary of State State Flag and Seal page',
    format: 'jpg',
    notes: 'Official JPEG retained locally and resized to a 512px display derivative.',
  },
  IL: {
    code: 'IL',
    status: 'verified-official',
    imagePath: '/flags/official/IL.jpg',
    imageUrl:
      'https://www.ilsos.gov/special/ifc/_jcr_content/root/container/container/container/columncontainer_51873568/col-2-2-1/image.coreimg.jpeg/1756223276365/flag1.jpeg',
    sourcePageUrl: 'https://www.ilsos.gov/special/ifc.html',
    sourceLabel: 'Illinois Secretary of State Flag Commission page',
    format: 'jpg',
    notes: 'Official JPEG retained locally and optimized for display.',
  },
  IN: {
    code: 'IN',
    status: 'verified-official',
    imagePath: '/flags/official/IN.png',
    imageUrl: 'https://www.in.gov/history/images/smsf.gif',
    sourcePageUrl: 'https://www.in.gov/history/about-indiana-history-and-trivia/emblems-and-symbols/',
    sourceLabel: 'Indiana state history emblems and symbols page',
    format: 'png',
    notes: 'Official GIF converted locally to PNG for app display.',
  },
  IA: {
    code: 'IA',
    status: 'verified-official',
    imagePath: '/flags/official/IA.png',
    imageUrl:
      'https://www.iowa.gov/sites/default/files/styles/large/public/images/2022-12/StateFlagofIowa.png.webp?itok=OQ07Pntv',
    sourcePageUrl: 'https://www.iowa.gov/official-website',
    sourceLabel: 'Iowa.gov official website page',
    format: 'png',
    notes:
      'Official Iowa.gov WebP source converted to a retained local PNG display derivative; source format and hashes are recorded in the acquisition manifest.',
  },
  MD: {
    code: 'MD',
    status: 'verified-official',
    imagePath: '/flags/official/MD.png',
    imageUrl: 'https://sos.maryland.gov/PublishingImages/mdflag_lg2.gif',
    sourcePageUrl: 'https://sos.maryland.gov/pages/services/mdflag.aspx',
    sourceLabel: 'Maryland Secretary of State flag page',
    format: 'png',
    notes: 'Official GIF converted locally to PNG for app display.',
  },
  ME: {
    code: 'ME',
    status: 'verified-official',
    imagePath: '/flags/official/ME.png',
    imageUrl: 'https://www.maine.gov/sos/kids/themes/kids/images/flag.gif',
    sourcePageUrl: 'https://www.maine.gov/sos/kids/about/symbols/flag',
    sourceLabel: 'Maine Secretary of State state flag page',
    format: 'png',
    notes: 'Official GIF converted locally to PNG for app display.',
  },
  MN: {
    code: 'MN',
    status: 'verified-official',
    imagePath: '/flags/official/MN.png',
    imageUrl: 'https://www.sos.mn.gov/media/uyej5hz1/mn_state_flag_final_spot.png',
    sourcePageUrl: 'https://www.sos.mn.gov/about-minnesota/state-symbols/state-flag/',
    sourceLabel: 'Minnesota Secretary of State state flag page',
    format: 'png',
  },
  MO: {
    code: 'MO',
    status: 'verified-official',
    imagePath: '/flags/official/MO.jpg',
    imageUrl: 'https://www.sos.mo.gov/CMSImages/Publications/symbols/flagthumb.jpg',
    sourcePageUrl: 'https://s1.sos.mo.gov/symbol/flag',
    sourceLabel: 'Missouri Secretary of State state flag page',
    format: 'jpg',
    notes: 'Official JPEG retained locally and resized to a 512px display derivative.',
  },
  MS: {
    code: 'MS',
    status: 'verified-official',
    imagePath: '/flags/official/MS.jpg',
    imageUrl: 'https://www.mdah.ms.gov/sites/default/files/2020-09/Chosen%20Final%20Flag.jpg',
    sourcePageUrl: 'https://www.mdah.ms.gov/taxonomy/term/131',
    sourceLabel: 'Mississippi Department of Archives and History state flag page',
    format: 'jpg',
    notes: 'Official state-owned JPEG retained locally and resized to a 512px display derivative.',
  },
  NC: {
    code: 'NC',
    status: 'verified-official',
    imagePath: '/flags/official/NC.jpg',
    imageUrl: 'https://files.nc.gov/nc/images/2021-11/nc_flag.jpg',
    sourcePageUrl: 'https://www.nc.gov/',
    sourceLabel: 'NC.gov official homepage',
    format: 'jpg',
  },
  ND: {
    code: 'ND',
    status: 'verified-official',
    imagePath: '/flags/official/ND.jpg',
    imageUrl: 'https://www.history.nd.gov/images/ND-Flag-Color.jpg',
    sourcePageUrl: 'https://www.history.nd.gov/state-flag.html',
    sourceLabel: 'State Historical Society of North Dakota state flag page',
    format: 'jpg',
  },
  NE: {
    code: 'NE',
    status: 'verified-official',
    imagePath: '/flags/official/NE.png',
    imageUrl: 'https://sos.nebraska.gov/sites/default/files/img/flag.png',
    sourcePageUrl: 'https://sos.nebraska.gov/state-symbols',
    sourceLabel: 'Nebraska Secretary of State state symbols page',
    format: 'png',
    notes: 'Official PNG retained locally and optimized for display.',
  },
  NJ: {
    code: 'NJ',
    status: 'verified-official',
    imagePath: '/flags/official/NJ.jpg',
    imageUrl: 'https://www.nj.gov/nj/library/about/facts_flag.jpg',
    sourcePageUrl: 'https://www.nj.gov/nj/about/symbols/',
    sourceLabel: 'New Jersey official state symbols page',
    format: 'jpg',
  },
  NM: {
    code: 'NM',
    status: 'verified-official',
    imagePath: '/flags/official/NM.jpg',
    imageUrl: 'https://www.sos.nm.gov/wp-content/uploads/2019/06/new-mexico-flag.jpg',
    sourcePageUrl: 'https://www.sos.nm.gov/about-new-mexico/state-flag/',
    sourceLabel: 'New Mexico Secretary of State state flag page',
    format: 'jpg',
  },
  NV: {
    code: 'NV',
    status: 'verified-official',
    imagePath: '/flags/official/NV.png',
    imageUrl: 'https://www.leg.state.nv.us/assembly/wp-content/uploads/2024/05/Flag.png',
    sourcePageUrl: 'https://www.leg.state.nv.us/assembly/visit/education/primary/nevadas-state-symbols/',
    sourceLabel: 'Nevada State Assembly state symbols page',
    format: 'png',
    notes: 'Official Nevada State Assembly PNG retained locally and converted to a display JPEG derivative.',
  },
  NY: {
    code: 'NY',
    status: 'verified-official',
    imagePath: '/flags/official/NY.jpg',
    imageUrl:
      'https://dos.ny.gov/sites/g/files/oee926/files/styles/wysiwyg/public/media/2021/05/state-flag-2020-002.jpg?itok=Zy0AL-Fp',
    sourcePageUrl: 'https://dos.ny.gov/new-york-state-emblems',
    sourceLabel: 'New York Department of State emblems page',
    format: 'jpg',
  },
  OR: {
    code: 'OR',
    status: 'verified-official',
    imagePath: '/flags/official/OR.jpg',
    imageUrl: 'https://images.oregon.gov/Portal/Images/Flags/oregon-flag.jpg',
    sourcePageUrl: 'https://sos.oregon.gov/blue-book/Pages/facts/symbols.aspx',
    sourceLabel: 'Oregon Secretary of State Blue Book state symbols page',
    format: 'jpg',
  },
  PA: {
    code: 'PA',
    status: 'verified-official',
    imagePath: '/flags/official/PA.jpg',
    imageUrl: 'https://s7d9.scene7.com/is/image/statepa/640%20flag_of_pennsylvania?ts=1740154244381&dpr=off',
    sourcePageUrl: 'https://www.pa.gov/agencies/dgs/flag-notices',
    sourceLabel: 'Pennsylvania Department of General Services flag notices page',
    format: 'jpg',
    notes: 'Official page serves the image from Pennsylvania state Scene7 CDN.',
  },
  RI: {
    code: 'RI',
    status: 'verified-official',
    imagePath: '/flags/official/RI.jpg',
    imageUrl: 'https://www.sos.ri.gov/assets/img/civics/ri-state-flag.jpg',
    sourcePageUrl: 'https://www.sos.ri.gov/divisions/civics-and-education/facts-and-figures/state-symbols',
    sourceLabel: 'Rhode Island Secretary of State state symbols page',
    format: 'jpg',
    notes: 'Official JPEG retained locally and resized to a 512px display derivative.',
  },
  SD: {
    code: 'SD',
    status: 'verified-official',
    imagePath: '/flags/official/SD.jpg',
    imageUrl: 'https://sdsos.gov/general-information/assets/state-emblems/Flag_of_South_Dakota.jpg',
    sourcePageUrl: 'https://sdsos.gov/general-information/about-state-south-dakota/state-seal-symbols.aspx',
    sourceLabel: 'South Dakota Secretary of State state seal and emblems page',
    format: 'jpg',
  },
  SC: {
    code: 'SC',
    status: 'verified-official',
    imagePath: '/flags/official/SC.png',
    imageUrl: 'https://www.sc.gov/modules/custom/flag_status/images/sc-full.png',
    sourcePageUrl: 'https://www.sc.gov/flag-status',
    sourceLabel: 'South Carolina.gov flag status page',
    format: 'png',
    notes: 'Official South Carolina.gov PNG retained locally and validated as a real image.',
  },
  TN: {
    code: 'TN',
    status: 'verified-official',
    imagePath: '/flags/official/TN.jpg',
    imageUrl:
      'https://sos-prod.tnsosgovfiles.com/s3fs-public/images/StateFlag.jpg?VersionId=0L12sT6T9zpSFM9bwVI.au6EUtkQeQ.O',
    sourcePageUrl: 'https://sos.tn.gov/civics/pages/state-symbols',
    sourceLabel: 'Tennessee Secretary of State state symbols page',
    format: 'jpg',
  },
  TX: {
    code: 'TX',
    status: 'verified-official',
    imagePath: '/flags/official/TX.jpg',
    imageUrl: 'https://www.tsl.texas.gov/sites/default/files/public/tslac/ref/images/texasflag.jpg',
    sourcePageUrl: 'https://www.tsl.texas.gov/ref/abouttx/flagdes',
    sourceLabel: 'Texas State Library and Archives state flag description page',
    format: 'jpg',
  },
  UT: {
    code: 'UT',
    status: 'verified-official',
    imagePath: '/flags/official/UT.png',
    imageUrl: 'https://flag.utah.gov/wp-content/uploads/2023/03/Utah_Flag_CMYK.png',
    sourcePageUrl: 'https://flag.utah.gov/content/',
    sourceLabel: 'Utah state flag program official content page',
    format: 'png',
    notes: 'Official standalone PNG retained locally and resized to a 512px display derivative.',
  },
  VA: {
    code: 'VA',
    status: 'verified-official',
    imagePath: '/flags/official/VA.png',
    imageUrl:
      'https://www.governor.virginia.gov/media/governorvirginiagov/governor-of-virginia/images/virginiaflag-full.gif',
    sourcePageUrl: 'https://www.governor.virginia.gov/constituent-services/flag-information/',
    sourceLabel: 'Governor of Virginia flag information page',
    format: 'png',
    notes:
      'Official Governor GIF converted to a retained local PNG display derivative; source format and hashes are recorded in the acquisition manifest.',
  },
  WA: {
    code: 'WA',
    status: 'verified-official',
    imagePath: '/flags/official/WA.png',
    imageUrl: 'https://www.sos.wa.gov/sites/default/files/2022-05/flag_seal.gif',
    sourcePageUrl: 'https://www.sos.wa.gov/washington-state-flag',
    sourceLabel: 'Washington Secretary of State flag graphics download',
    format: 'png',
    notes: 'Official GIF converted locally to PNG for app display.',
  },
  WV: {
    code: 'WV',
    status: 'verified-official',
    imagePath: '/flags/official/WV.jpg',
    imageUrl: 'https://governor.wv.gov/sites/default/files/2024-12/flag.jpg',
    sourcePageUrl: 'https://governor.wv.gov/basic-block/flag-status',
    sourceLabel: 'West Virginia Governor flag status page',
    format: 'jpg',
    notes: 'Official JPEG retained locally and resized to a 512px display derivative.',
  },
  WI: {
    code: 'WI',
    status: 'verified-official',
    imagePath: '/flags/official/WI.jpg',
    imageUrl: 'https://www.wisconsin.gov/PublishingImages/State_Flag.jpg',
    sourcePageUrl: 'https://www.wisconsin.gov/Pages/WisconsinforKids.aspx',
    sourceLabel: 'Wisconsin.gov state symbols page',
    format: 'jpg',
    notes: 'Official JPEG retained locally and resized to a 512px display derivative.',
  },
  WY: {
    code: 'WY',
    status: 'verified-official',
    imagePath: '/flags/official/WY.jpg',
    imageUrl: 'https://sos.wyo.gov/images/Symbols/State-Flag.jpg',
    sourcePageUrl: 'https://sos.wyo.gov/Services/StateInfo_Symbols.aspx',
    sourceLabel: 'Wyoming Secretary of State state symbols page',
    format: 'jpg',
  },
};

export function getStateFlagAsset(code?: string): StateFlagAsset {
  const normalizedCode = code?.toUpperCase() || '';
  const officialAsset = STATE_FLAG_ASSETS[normalizedCode];

  if (officialAsset?.status === 'verified-official' && officialAsset.imagePath) {
    return officialAsset;
  }

  const wikimediaAsset = WIKIMEDIA_FLAG_ASSETS[normalizedCode];

  if (wikimediaAsset) {
    const pendingNote = officialAsset?.notes
      ? `${officialAsset.notes} Wikimedia Commons fallback is used for display while direct official PNG/JPG sourcing remains pending.`
      : 'Wikimedia Commons fallback is used for display while direct official state-government PNG/JPG sourcing remains pending.';

    return {
      code: normalizedCode,
      status: 'wikimedia-fallback',
      imagePath: wikimediaAsset.imagePath,
      imageUrl: wikimediaAsset.imageUrl,
      sourcePageUrl: wikimediaAsset.sourcePageUrl,
      sourceLabel: 'Wikimedia Commons flag render',
      format: 'png',
      notes: pendingNote,
    };
  }

  return (
    officialAsset ?? {
      code: normalizedCode,
      status: 'needs-official-source',
      sourceLabel: 'Official state government PNG/JPG source pending',
      notes: 'No flag artwork is shown until a state .gov PNG/JPG source or approved fallback is verified.',
    }
  );
}

export function getVerifiedOfficialFlagCount(): number {
  return Object.values(STATE_FLAG_ASSETS).filter((asset) => asset.status === 'verified-official').length;
}

export function getKnownOfficialFlagSourceCount(): number {
  return Object.values(STATE_FLAG_ASSETS).filter((asset) => asset.status !== 'needs-official-source').length;
}

export function getWikimediaFallbackFlagCount(): number {
  return Object.keys(WIKIMEDIA_FLAG_ASSETS).filter((code) => STATE_FLAG_ASSETS[code]?.status !== 'verified-official')
    .length;
}

export function getRenderableFlagCount(): number {
  const officialCodes = Object.values(STATE_FLAG_ASSETS)
    .filter((asset) => asset.status === 'verified-official' && asset.imagePath)
    .map((asset) => asset.code);

  return new Set([...officialCodes, ...Object.keys(WIKIMEDIA_FLAG_ASSETS)]).size;
}
